import { Environment, SgAuthAdapter } from '@sg/common';
import { isTestEnvironment } from '@sg/common/src/lib/utils/test-environment';
import { SgAppConfig } from '../app-bootstrap';
import { AppEnvironmentApi } from './base';
import { isDemoMode } from './demo-flag';

// The sales deploy pipeline builds with `--configuration=ci`, then a container
// startup script (`nginx/set-environment.sh`) mutates `assets/environment.json`
// based on `SGUI_*` env vars and the runtime `identityReady()` does
// `Object.assign(environment, data)` to overlay those values.
//
// When the prospect demo flag is set (`?demo=…` URL param or `sgDemo` in
// sessionStorage), we need to:
//   1. flip `enableMocking` to true so MSW starts
//   2. set `env` to 'demo' so the watermark + branding helpers handle the
//      demo surface correctly
//   3. flip `async` to false so the runtime environment.json fetch doesn't
//      blow away our `enableMocking: true` with the JSON file's defaults
//
// Normal visitors (no demo flag) get the original ci behavior unchanged.
const DEMO_ACTIVE = isDemoMode();

const environment: Environment = {
  async: !DEMO_ACTIVE,
  production: !DEMO_ACTIVE,
  env: DEMO_ACTIVE ? 'demo' : 'prod',
  enableMocking: DEMO_ACTIVE,
  oidc: {
    authority: 'https://api-dev.sharegain.com/identity',
    client_id: 'js',
    filterProtocolClaims: true,
    loadUserInfo: true,
    automaticSilentRenew: false,
    silentRequestTimeout: 10000,
    prompt: 'login',
  },
  oidcRenewPolicy: {
    interval: 10,
    renewWhenExpiresIn: 50,
    retries: 2,
  },
  api: {
    SHARED: {
      basePath: 'https://api-dev.sharegain.com',
      withCredentials: true,
    },
  },
  ws: {
    baseUrl: 'https://api-dev.sharegain.com/signalr',
  },
  i18n: {
    default: 'en',
    langs: {
      en: {
        key: 'en',
        globalName: 'English',
      },
      he: {
        key: 'he',
        globalName: 'Hebrew',
        localName: 'עברית',
        rtl: true,
      },
    },
  },
  idle: [60, 360],
  featureFlag: {
    isLegalEntityResourceHubSettingsDisplayed: true,
    isLegalEntitySmartPortfolioInsightsSettingsDisplayed: true,
    isOneClickAnalyzerEnabled: true,
  },
  hotjar: {},
  heap: {},
  pendo: {},
  loggly: {
    key: '431152b9-7c4d-4ffb-b96b-6f7a502540a3',
    tag: 'development',
  },
};

// Login bypass covers two paths:
//   - Cypress tests: `window.Cypress.__AUTH_ADAPTER__` is set by the test runner.
//   - Prospect demo: MSW's `setupAuthBypass()` sets both `window.playwright = true`
//     and `window.__AUTH_ADAPTER__` so the no-auth flow short-circuits OIDC.
const login = {
  byPass(_appConfig: SgAppConfig): boolean {
    const w = window as any;
    if (w.Cypress && w.Cypress['__AUTH_ADAPTER__']) return true;
    if (isTestEnvironment() && w['__AUTH_ADAPTER__']) return true;
    return false;
  },
  adapter(_appConfig: SgAppConfig, _env: Environment): SgAuthAdapter {
    const w = window as any;
    return (w.Cypress && w.Cypress['__AUTH_ADAPTER__']) || w['__AUTH_ADAPTER__'];
  },
};

export const envApi: AppEnvironmentApi = {
  environment,
  login,
};
